// worker-decisions-g0-delegation-claim-guard.ts — phase 06d R1/R2 (dogfood
// lần 3, F-3; spec V2 §3 E17).
//
// Only the controller applies the G0 delegation (`decision_auto_applied`). Run3
// seq 92/95: T-0004 asked a real question AND, on the same result, declared
// `decisions[].policy = "G0 decision_policy=auto"` for another choice it made
// itself — the router took the needs_decision branch first, so the done-path
// self-amend guard never saw it.
//   R1: a claim in `decisions[]` (5 keys) while verdict ∈ {done, rejected} and
//       worker outcome ∈ {done, needs_decision} ⇒ no interview, no commit:
//       retry as `task_failed`, or escalate when the running ticket's own
//       acceptance handed the decision to the worker (retry would only repeat it).
//   R2: a `plan` done whose create/amend acceptance delegates ⇒ escalate
//       (wired in supervisor-outcome-router-done-path.ts).
// This file = the PURE predicates; the R1 route (journal/retry/escalate) lives in
// supervisor-outcome-router-g0-delegation-claim-path.ts so the done path can
// import R2 from here without an import cycle.
// Exempt per VALUE: a BLUEPRINT ledger quote `QĐ-\d+` without G0/decision_policy
// (B6); an exact quote of THIS ticket's delegated answer (`answered_by` or its
// question id) (B12). Prose keys (`decision`/`rationale`/`summary`) are never read.
import type { AutopilotResult } from "./autopilot-result-schema.js";
import { redactEvidence } from "./retry-policy-evidence.js";

const CLAIM_KEYS = ["policy", "decided_by", "authority", "basis", "source"] as const;
const DELEGATION_CLAIM = /\bG0\b|decision_policy|delegat|ủy nhiệm|uỷ nhiệm|uy nhiem/i;
const SELF_DECIDE = /tự quyết|tự chọn|self[- ]decide/i;
const LEDGER_QUOTE = /^\s*QĐ-\d+\b/;
const G0_OR_POLICY = /\bG0\b|decision_policy/i;
/** Clip for a worker-written acceptance text quoted in a reason. */
export const ACCEPTANCE_CLIP_CHARS = 200;

export interface G0DelegationClaim {
  id: string;
  key: string;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

/** NFC first: a decomposed "ủy" must match like a composed one. */
function claimsDelegation(value: string): boolean {
  return DELEGATION_CLAIM.test(value.normalize("NFC"));
}

/** First record in `result.decisions[]` with a non-exempt delegation claim; `undefined` = none. */
export function findG0DelegationClaim(
  result: unknown,
  exemptValues: ReadonlySet<string> = new Set(),
): G0DelegationClaim | undefined {
  if (!isRecord(result) || !Array.isArray(result.decisions)) return undefined;
  for (const [index, record] of result.decisions.entries()) {
    if (!isRecord(record)) continue;
    for (const key of CLAIM_KEYS) {
      const value = record[key];
      if (typeof value !== "string" || !claimsDelegation(value)) continue;
      const nfc = value.normalize("NFC");
      if (LEDGER_QUOTE.test(nfc) && !G0_OR_POLICY.test(nfc)) continue; // B6
      if (exemptValues.has(value.trim())) continue; // B12
      return { id: typeof record.id === "string" ? record.id : `decisions[${index}]`, key };
    }
  }
  return undefined;
}

function acceptanceText(element: unknown): string | undefined {
  if (typeof element === "string") return element;
  return isRecord(element) && typeof element.text === "string" ? element.text : undefined;
}

/** First acceptance element (string or `{text}`) that hands an open decision to the worker (N6: one element). */
export function acceptanceDelegatingToWorker(acceptance: unknown): string | undefined {
  if (!Array.isArray(acceptance)) return undefined;
  for (const element of acceptance) {
    const text = acceptanceText(element);
    if (text !== undefined && claimsDelegation(text) && SELF_DECIDE.test(text.normalize("NFC"))) {
      return text;
    }
  }
  return undefined;
}

/**
 * R2: reason when a create/amend in `proposed_changes` carries a delegating
 * acceptance. Worker-written text is masked here, THEN clipped (review L3):
 * escalate() stores the reason as-is.
 */
export function findPlanAcceptanceDelegatingToWorker({
  proposedChanges,
  secretValues = [],
}: {
  proposedChanges: AutopilotResult["proposed_changes"];
  secretValues?: readonly string[];
}): string | undefined {
  for (const change of proposedChanges) {
    if (change.op !== "create" && change.op !== "amend") continue;
    const content = isRecord(change.content) ? change.content : {};
    const text = acceptanceDelegatingToWorker(content.acceptance);
    if (text === undefined) continue;
    const quoted = redactEvidence(text, secretValues).slice(0, ACCEPTANCE_CLIP_CHARS);
    return (
      `plan_delegates_open_decision_to_worker_rejected: ${redactEvidence(change.target, secretValues)} — acceptance ` +
      `${JSON.stringify(quoted)} giao worker tự quyết theo G0; ` +
      "chỉ controller áp G0 — viết acceptance dạng `gặp quyết định X ⇒ trả needs_decision (options + khuyến nghị)`"
    );
  }
  return undefined;
}
